import { inject, Injectable } from '@angular/core';
import {
    AsnLineDto,
    AsnLineFacade,
    ReceiptHeaderDto,
    ReceiptHeaderFacade,
    ReceiptLineDto,
    ReceiptLineFacade,
} from '@pf/platform-wms/domain';
import { PurchaseOrderHeaderDto } from '@pf/platform-orders/domain';
import { AsnStatus, ReceiptStatus, RequisitionLineFragment, } from '@control-tower/platform-wms';
import { catchError, forkJoin, map, Observable, of, shareReplay, switchMap, } from 'rxjs';
import { flatMap, isEmpty } from 'lodash-es';
import { PoLineAsnShipment, PoLineSummary, } from '../models/po-line-summary.model';
import { LineConfirmSelection, ReceiptLineMapping, } from '../models/receiving-context.model';
import { LoggerService } from '@pf/shared-services';
import { asArraySafe } from '@pf/shared-utility';
import { PurchaseOrderLineViewModelRead } from '@control-tower/platform-orders';

/** Cache key used for receipt headers that have no associated ASN. */
const UNIDENTIFIED_KEY = '__null__';

/**
 * Manages receipt-header and receipt-line creation during the receiving workflow.
 *
 * Handles confirming selected PO lines by creating (or reusing) receipt headers
 * and lines, updating ASN line statuses, and patching actual quantities on
 * receipt lines after counting.
 */
@Injectable()
export class ReceivingReceiptService {
    private readonly receiptHeaderFacade = inject(ReceiptHeaderFacade);
    private readonly receiptLineFacade = inject(ReceiptLineFacade);
    private readonly asnLineFacade = inject(AsnLineFacade);
    private readonly logger = inject(LoggerService);

    /** Per-call cache of receipt-header observables keyed by ASN header ID. */
    private _headerCache = new Map<string, Observable<string>>();

    /**
     * Confirms selected PO lines by creating or reusing receipt headers and lines.
     *
     * For each selected line the service either:
     * - reuses an existing unreceived receipt, or
     * - creates a new receipt header (cached per ASN) and receipt line.
     *
     * @returns An observable emitting the flat array of {@link ReceiptLineMapping}s.
     */
    confirmLines$(
        selections: LineConfirmSelection[],
        lines: PoLineSummary[],
        po: PurchaseOrderHeaderDto,
        warehouseId: string
    ): Observable<ReceiptLineMapping[]> {
        if (isEmpty(selections)) {
            this.logger.info('confirmLines$ called with no selections');
            return of([]);
        }

        this._headerCache.clear();

        const lineOps$ = selections.map(selection => {
            const line = lines.find(l => l.id === selection.lineId);
            if (!line) {
                this.logger.warn(
                    `confirmLines$ – line ${selection.lineId} not found`
                );
                return of([] as ReceiptLineMapping[]);
            }
            return this.confirmLine$(line, selection, po, warehouseId);
        });

        return forkJoin(lineOps$).pipe(
            map(results => flatMap(results)),
            catchError(err => {
                this.logger.error(
                    `Failed to confirm receipt lines for PO ${po.id}`,
                    err
                );
                return of([]);
            })
        );
    }

    /**
     * Updates the status of an ASN line (e.g. InTransit → Delivered).
     *
     * @param asnLineId   The ASN line to update.
     * @param asnHeaderId The parent ASN header.
     * @param status      The new {@link AsnStatus} value.
     */
    updateAsnLineStatus$(
        asnLineId: string,
        asnHeaderId: string,
        status: AsnStatus
    ): Observable<AsnLineDto> {
        return this.asnLineFacade.getById$(asnLineId, asnHeaderId).pipe(
            switchMap(existing => {
                const dto = new AsnLineDto({
                    ...(existing as Partial<AsnLineDto>),
                    status,
                });
                return this.asnLineFacade.save$(dto);
            }),
            catchError(err => {
                this.logger.error(
                    `Failed to update ASN line ${asnLineId} status to ${status}`,
                    err
                );
                throw err;
            })
        );
    }

    /**
     * Patches the actual counted quantity on a receipt line.
     *
     * @param receiptLineId   The receipt line to update.
     * @param receiptHeaderId The parent receipt header.
     * @param actualQuantity  The counted quantity.
     */
    updateReceiptLineActualQuantity$(
        receiptLineId: string,
        receiptHeaderId: string,
        actualQuantity: number
    ): Observable<ReceiptLineDto> {
        return this.receiptLineFacade
            .getById$(receiptLineId, receiptHeaderId)
            .pipe(
                switchMap(existing => {
                    const dto = new ReceiptLineDto({
                        ...(existing as Partial<ReceiptLineDto>),
                        actualQuantity,
                    });
                    return this.receiptLineFacade.save$(dto);
                }),
                catchError(err => {
                    this.logger.error(
                        `Failed to update actual quantity for receipt line ${receiptLineId}`,
                        err
                    );
                    throw err;
                })
            );
    }

    /**
     * Confirms a single PO line based on the user's ASN selections.
     * Creates receipt lines only for the selected ASN shipments and/or
     * an unidentified receipt line when {@link LineConfirmSelection.includeWithoutAsn} is true.
     */
    private confirmLine$(
        line: PoLineSummary,
        selection: LineConfirmSelection,
        po: PurchaseOrderHeaderDto,
        warehouseId: string
    ): Observable<ReceiptLineMapping[]> {
        const selectedShipments = line.asnShipments.filter(
            s =>
                selection.asnHeaderIds.includes(s.asnHeaderId) &&
                (s.status === AsnStatus.InTransit ||
                    s.status === AsnStatus.Delivered)
        );

        const ops$ = selectedShipments.map(shipment =>
            this.confirmShipment$(line, shipment, po, warehouseId)
        );

        if (selection.includeWithoutAsn) {
            ops$.push(
                this.createUnidentifiedReceiptLine$(line, po, warehouseId)
            );
        }

        if (ops$.length === 0) {
            return of([]);
        }

        return forkJoin(ops$).pipe(map(results => flatMap(results)));
    }

    /**
     * Confirms a single ASN shipment for a PO line.
     * Reuses an existing unreceived receipt or creates a new one.
     */
    private confirmShipment$(
        line: PoLineSummary,
        shipment: PoLineAsnShipment,
        po: PurchaseOrderHeaderDto,
        warehouseId: string
    ): Observable<ReceiptLineMapping[]> {
        const existing = line.receipts.find(
            r => !r.isReceived && r.asnHeaderId === shipment.asnHeaderId
        );

        if (existing) {
            return of([
                this.buildMapping(
                    existing.receiptHeaderId,
                    existing.receiptLineId,
                    line.id,
                    shipment.asnHeaderId
                ),
            ]);
        }

        return this.getOrCreateReceiptHeader$(
            po,
            warehouseId,
            shipment.asnHeaderId
        ).pipe(
            switchMap(headerId =>
                this.createReceiptLine$(
                    line,
                    headerId,
                    shipment.shippedQuantity ?? 0,
                    shipment.asnHeaderId,
                    shipment.asnLineId
                )
            ),
            map(({ headerId, lineId }) => [
                this.buildMapping(
                    headerId,
                    lineId,
                    line.id,
                    shipment.asnHeaderId
                ),
            ])
        );
    }

    /** Builds a {@link ReceiptLineMapping} for a confirmed receipt line. */
    private buildMapping(
        receiptHeaderId: string,
        receiptLineId: string,
        poLineId: string,
        asnHeaderId: string | null
    ): ReceiptLineMapping {
        return {
            receiptHeaderId,
            receiptLineId,
            poLineId,
            asnHeaderId,
        };
    }

    private createUnidentifiedReceiptLine$(
        line: PoLineSummary,
        po: PurchaseOrderHeaderDto,
        warehouseId: string
    ): Observable<ReceiptLineMapping[]> {
        const existing = line.receipts.find(
            r =>
                !r.asnHeaderId &&
                !r.isReceived &&
                r.status !== ReceiptStatus.Received &&
                !r.isPutaway
        );
        const expectedQty = line.orderedQuantity - line.receivedQuantity;

        if (existing) {
            return this.updateUnidentifiedReceiptLine$(
                line,
                existing,
                expectedQty
            );
        }

        return this.createNewUnidentifiedReceiptLine$(
            line,
            po,
            warehouseId,
            expectedQty
        );
    }

    private updateUnidentifiedReceiptLine$(
        line: PoLineSummary,
        existing: PoLineSummary['receipts'][number],
        expectedQty: number
    ): Observable<ReceiptLineMapping[]> {
        return this.receiptLineFacade
            .getById$(existing.receiptLineId, existing.receiptHeaderId)
            .pipe(
                switchMap(existingLine =>
                    this.receiptLineFacade.save$(
                        new ReceiptLineDto({
                            ...(existingLine as Partial<ReceiptLineDto>),
                            expectedQuantity: expectedQty,
                        })
                    )
                ),
                map(() => [
                    this.buildMapping(
                        existing.receiptHeaderId,
                        existing.receiptLineId,
                        line.id,
                        null
                    ),
                ])
            );
    }

    private createNewUnidentifiedReceiptLine$(
        line: PoLineSummary,
        po: PurchaseOrderHeaderDto,
        warehouseId: string,
        expectedQty: number
    ): Observable<ReceiptLineMapping[]> {
        return this.getOrCreateReceiptHeader$(po, warehouseId, null).pipe(
            switchMap(headerId =>
                this.createReceiptLine$(line, headerId, expectedQty)
            ),
            map(({ headerId, lineId }) => [
                this.buildMapping(headerId, lineId, line.id, null),
            ])
        );
    }

    /**
     * Creates a new receipt line under the given header.
     *
     * @returns An observable emitting the header ID and the newly created line ID.
     */
    private createReceiptLine$(
        line: PoLineSummary,
        headerId: string,
        expectedQuantity: number,
        asnHeaderId?: string | null,
        asnLineId?: string | null
    ): Observable<{ headerId: string; lineId: string }> {
        const receiptLine = new ReceiptLineDto({
            id: '',
            inventoryReceiptId: headerId,
            status: ReceiptStatus.Pending,
            expectedQuantity,
            vendorLPN: line.vendorLPN,
            purchaseOrderHeader: { id: line.purchaseOrderHeaderId },
            purchaseOrderLine: { id: line.id },
            item: { id: line.itemId },
            advanceShippingNoticeId: asnHeaderId ?? null,
            advanceShippingNoticeLineId: asnLineId ?? null,
        } as Partial<ReceiptLineDto>);

        return this.receiptLineFacade
            .save$(receiptLine)
            .pipe(map(saved => ({ headerId, lineId: saved.id })));
    }

    /**
     * Creates a new receipt header and receipt line for a PO without an
     * existing task context. Used when an exception is raised during PO
     * lookup and the product needs to be received directly to a
     * non-compliance slot.
     *
     * @returns An observable emitting the created header ID and line ID.
     */
    createReceiptHeaderAndLine$(
        po: PurchaseOrderHeaderDto,
        warehouseId: string,
        requisitionLine: RequisitionLineFragment | null | undefined,
        expectedQuantity: number
    ): Observable<{ headerId: string; lineId: string }> {
        const poLine = asArraySafe(po.purchaseOrderLines).find(
            line => line.item?.id === requisitionLine?.item?.id
        );

        return this.receiptHeaderFacade
            .save$(
                new ReceiptHeaderDto({
                    id: '',
                    poNumber: po.orderNumber,
                    asnHeaderId: null,
                    warehouse: { id: warehouseId },
                    vendor: po.vendor,
                    status: ReceiptStatus.Pending,
                } as Partial<ReceiptHeaderDto>)
            )
            .pipe(
                switchMap(savedHeader =>
                    this.saveReceiptLine$(
                        savedHeader.id,
                        po,
                        poLine,
                        requisitionLine,
                        expectedQuantity
                    )
                ),
                catchError(err => {
                    this.logger.error(
                        `Failed to create receipt header and line for PO ${po.id}`,
                        err
                    );
                    throw err;
                })
            );
    }

    private saveReceiptLine$(
        headerId: string,
        po: PurchaseOrderHeaderDto,
        poLine: PurchaseOrderLineViewModelRead | undefined,
        requisitionLine: RequisitionLineFragment | null | undefined,
        expectedQuantity: number
    ): Observable<{ headerId: string; lineId: string }> {
        const receiptLine = new ReceiptLineDto({
            id: '',
            inventoryReceiptId: headerId,
            status: ReceiptStatus.Pending,
            expectedQuantity,
            actualQuantity: expectedQuantity,
            purchaseOrderHeader: { id: po.id },
            purchaseOrderLine: poLine,
            item: requisitionLine?.item?.id
                ? { id: requisitionLine.item.id }
                : undefined,
            vendorLPN: 'unknown',
        } as Partial<ReceiptLineDto>);

        return this.receiptLineFacade
            .save$(receiptLine)
            .pipe(map(savedLine => ({ headerId, lineId: savedLine.id })));
    }

    /**
     * Returns a cached receipt-header observable or creates a new header.
     *
     * Headers are cached per ASN header ID (or {@link UNIDENTIFIED_KEY} for
     * lines without an ASN) so that multiple lines sharing the same ASN
     * reuse a single header.
     */
    private getOrCreateReceiptHeader$(
        po: PurchaseOrderHeaderDto,
        warehouseId: string,
        asnHeaderId: string | null
    ): Observable<string> {
        const cacheKey = asnHeaderId ?? UNIDENTIFIED_KEY;
        const cached = this._headerCache.get(cacheKey);
        if (cached) {
            return cached;
        }

        const header$ = this.receiptHeaderFacade
            .save$(
                new ReceiptHeaderDto({
                    id: '',
                    poNumber: po.orderNumber,
                    asnHeaderId,
                    warehouse: { id: warehouseId },
                    vendor: po.vendor,
                    status: ReceiptStatus.Pending,
                } as Partial<ReceiptHeaderDto>)
            )
            .pipe(
                map(saved => saved.id),
                shareReplay(1)
            );

        this._headerCache.set(cacheKey, header$);
        return header$;
    }
}
